/**
 * Eigenbau: Suchfenster zu ⌘K — Tremors CommandBar ist eine Aktionsleiste.
 *
 * Der Entwurf setzt in die Kopfleiste ein Suchfeld mit `⌘K`. Im Bestand liegt
 * `CommandBar`, aber das ist etwas anderes: eine schwebende Leiste am unteren
 * Rand für Mengenaktionen auf ausgewählte Zeilen (`Popover` mit Zähler und
 * Knöpfen). Ein Suchfenster mit Feld und Trefferliste ist es nicht.
 *
 * Gebaut auf `Dialog` aus dem Bestand, also auf Radix: Fokusfalle, Escape,
 * `aria-modal` und das Zurückgeben des Fokus kommen von dort. Selbst gebaut ist
 * nur, was der Entwurf zeichnet.
 *
 * Der Suchdienst liefert die Planobjekte und Seiten aller sichtbaren Projekte,
 * serverseitig auf die Sicht eingeschränkt.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { api } from "@/lib/api";
import { cx, focusRing } from "@/lib/utils";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";

type Treffer = {
  art:
    | "aufgabe"
    | "seite"
    | "meilenstein"
    | "arbeitspaket"
    | "akzeptanzkriterium";
  id: string;
  titel: string;
  projektId: string;
  zielId?: string;
};

const ART_LABEL: Record<Treffer["art"], string> = {
  aufgabe: "Aufgabe",
  seite: "Seite",
  meilenstein: "Meilenstein",
  arbeitspaket: "Arbeitspaket",
  akzeptanzkriterium: "Akzeptanz",
};

export function Suchfenster({
  offen,
  setzeOffen,
}: {
  offen: boolean;
  setzeOffen: (wert: boolean) => void;
}) {
  const [begriff, setzeBegriff] = useState("");
  const [markiert, setzeMarkiert] = useState(0);
  const navigate = useNavigate();
  const feld = useRef<HTMLInputElement>(null);

  /*
   * Entprellt. Ohne Verzögerung schickt jeder Tastendruck eine Abfrage, und
   * die Suche läuft über zwei Volltextspalten — bei „Merge" wären das fünf
   * Abfragen für ein Wort.
   */
  const [entprellt, setzeEntprellt] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setzeEntprellt(begriff.trim()), 180);
    return () => clearTimeout(t);
  }, [begriff]);

  const treffer = useQuery({
    queryKey: ["cubic", "suche", entprellt],
    queryFn: () => api.suche.finden.query({ begriff: entprellt }),
    enabled: offen && entprellt.length > 0,
  });

  const liste = useMemo<readonly Treffer[]>(
    () => (treffer.data ?? []).slice(0, 12),
    [treffer.data],
  );

  // Beim Öffnen leer anfangen und die Markierung zurücksetzen — ein Fenster,
  // das den alten Begriff zeigt, sucht etwas, das niemand gerade gefragt hat.
  useEffect(() => {
    if (offen) {
      setzeBegriff("");
      setzeEntprellt("");
      setzeMarkiert(0);
    }
  }, [offen]);

  useEffect(() => {
    setzeMarkiert(0);
  }, [entprellt]);

  const oeffne = (t: Treffer) => {
    setzeOffen(false);
    if (t.art === "aufgabe") {
      void navigate({ to: "/aufgaben/$id", params: { id: t.id } });
    } else if (t.art === "akzeptanzkriterium" && t.zielId) {
      void navigate({ to: "/aufgaben/$id", params: { id: t.zielId } });
    } else if (t.art === "seite") {
      void navigate({
        to: "/projekte/$projektId/seiten/$seiteId",
        params: { projektId: t.projektId, seiteId: t.id },
      });
    } else if (t.art === "meilenstein") {
      void navigate({
        to: "/projekte/$projektId/meilensteine/$meilensteinId",
        params: { projektId: t.projektId, meilensteinId: t.id },
      });
    } else if (t.art === "arbeitspaket") {
      void navigate({
        to: "/projekte/$projektId/arbeitspakete/$arbeitspaketId",
        params: { projektId: t.projektId, arbeitspaketId: t.id },
      });
    }
  };

  return (
    <DialogPrimitive.Root open={offen} onOpenChange={setzeOffen}>
      <DialogPrimitive.Portal>
        {/*
          Keine Rundung, kein Schatten — die Fläche und eine Haarlinie tragen
          die Kante, wie überall im Entwurf. Der Überzug ist Tinte mit
          Durchlass, damit sichtbar bleibt, dass darunter die Seite weiterläuft.
        */}
        <DialogPrimitive.Overlay className="animate-dialogOverlayShow fixed inset-0 z-50 bg-tinte/25" />
        <DialogPrimitive.Content
          className={cx(
            "animate-dialogContentShow fixed left-1/2 top-[15%] z-50 w-[92vw] max-w-[560px]",
            "-translate-x-1/2 border border-linie bg-papier",
          )}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            feld.current?.focus();
          }}
        >
          <DialogPrimitive.Title className="sr-only">Suchen</DialogPrimitive.Title>

          <div className="flex items-center gap-2.5 border-b border-linie px-4">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="square"
              aria-hidden="true"
              className="flex-none text-tinte4"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              ref={feld}
              value={begriff}
              onChange={(e) => setzeBegriff(e.target.value)}
              placeholder="Plan und Seiten durchsuchen"
              aria-label="Suchbegriff"
              // Eigene Tastenführung: Pfeile durch die Liste, Enter öffnet.
              // Escape nimmt Radix.
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setzeMarkiert((i) => Math.min(i + 1, Math.max(liste.length - 1, 0)));
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setzeMarkiert((i) => Math.max(i - 1, 0));
                }
                if (e.key === "Enter") {
                  const t = liste[markiert];
                  if (t) {
                    e.preventDefault();
                    oeffne(t);
                  }
                }
              }}
              className="h-12 min-w-0 flex-1 border-0 bg-transparent text-[15px] font-light text-tinte outline-none placeholder:text-tinte4"
            />
            <Mikrolabel grad={11.5} className="flex-none">
              ⌘K
            </Mikrolabel>
          </div>

          <div className="max-h-[50vh] overflow-y-auto">
            {entprellt.length === 0 ? (
              <p className="px-4 py-5">
                <Mikrolabel>Tippen, um zu suchen.</Mikrolabel>
              </p>
            ) : treffer.isLoading ? (
              <p className="px-4 py-5">
                <Mikrolabel>Wird gesucht …</Mikrolabel>
              </p>
            ) : liste.length === 0 ? (
              <p className="px-4 py-5">
                <Mikrolabel>Nichts gefunden zu „{entprellt}".</Mikrolabel>
              </p>
            ) : (
              <ul role="list">
                {liste.map((t, i) => (
                  <li key={`${t.art}-${t.id}`}>
                    <button
                      type="button"
                      onClick={() => oeffne(t)}
                      onMouseEnter={() => setzeMarkiert(i)}
                      className={cx(
                        "flex h-11 w-full items-center gap-3 border-b border-linie px-4 text-left",
                        i === markiert && "bg-wisch",
                        focusRing,
                      )}
                    >
                      {/* Die Art steht als Wort da, nicht als Symbol — vier
                          Buchstaben sagen mehr als ein Piktogramm. */}
                      <Mikrolabel grad={11} className="w-[52px] flex-none">
                        {ART_LABEL[t.art]}
                      </Mikrolabel>
                      <span className="min-w-0 flex-1 truncate text-[14px]" title={t.titel}>
                        {t.titel}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
